import type {
	BBModel,
	BBModelChild,
	CardData,
	CardFloatChild,
	StoryElementReference,
	StoryVersionType,
} from 'types/story';
import { CARD_ROOT_ELEMENT_INDEX, COMPONENT_TYPE, LAYER_TYPE, STORY_ROOT_ELEMENT_INDEX } from 'common/constants';
import { findComponentBy } from './find-component-by';

export function isStoryElementReference(value: unknown): value is StoryElementReference {
	return (
		typeof value === 'object' &&
		value !== null &&
		'$storyElement' in value &&
		typeof value.$storyElement === 'string'
	);
}

export function createStoryElementReference(id: string): StoryElementReference {
	return { $storyElement: id };
}

export function getStoryElementId(value: CardFloatChild): string | undefined {
	return isStoryElementReference(value) ? value.$storyElement : value && '_id' in value ? value._id : undefined;
}

export function resolveStoryElement(
	reference: StoryElementReference,
	storyElements: BBModel[] = []
): BBModel | undefined {
	for (const root of storyElements) {
		if (Array.isArray(root?.children)) {
			const model = root.children.find(
				(child): child is BBModel => !!child && '_id' in child && child._id === reference.$storyElement
			);
			if (model) return model;
		}
	}
	return undefined;
}

/** Legacy full card copies are ordering placements when their ID has a canonical owner. */
export function isStoryElementPlacement(value: CardFloatChild, storyElements: BBModel[] = []): boolean {
	if (isStoryElementReference(value)) return true;
	const id = getStoryElementId(value);
	return id !== undefined && resolveStoryElement(createStoryElementReference(id), storyElements) !== undefined;
}

/** Resolve canonical payloads at card positions, then append missing globals for recovery. */
export function combineStoryElements(cardChildren: CardFloatChild[], storyChildren: BBModel[]): BBModelChild[] {
	const canonical = new Map(storyChildren.map(model => [model._id, model]));
	const seen = new Set<string>();
	const result: BBModelChild[] = [];
	for (const child of cardChildren) {
		const id = getStoryElementId(child);
		const model =
			(id === undefined ? undefined : canonical.get(id)) ?? (isStoryElementReference(child) ? undefined : child);
		if (!model || (id !== undefined && canonical.has(id) && seen.has(id))) continue;
		result.push(model);
		if (id !== undefined) seen.add(id);
	}
	for (const model of storyChildren) {
		if (!seen.has(model._id)) result.push(model);
	}
	return result;
}

/** Normalize first-level ordering entries only. Layer flags do not establish ownership.
 * For each float root, it walks the card’s children in order:
 * - If a child’s ID belongs to a global element, it keeps one reference to that ID.
 * 	 This also serializes resolved editor models back into references.
 * - If it is a local block, it keeps the block.
 * - If it is a reference to a missing global, or a duplicate reference, it drops it.
 * - It appends references for globals missing from the card.
 * It changes root.children only when the result differs, so running it again has no effect.
 * It checks only immediate float-root children, not nested blocks. Pass the story’s elements:
 * omitting storyElements defaults to [], which makes every existing reference appear dangling and removes it.
 */
export function synchronizeStoryElementReferences(card: CardData, storyElements: BBModel[] = []): void {
	normalizeStoryElementReferences(card, storyElements, false);
}

function normalizeStoryElementReferences(card: CardData, storyElements: BBModel[], migrateLegacy: boolean): void {
	for (const type of [COMPONENT_TYPE.FLOAT_ABOVE, COMPONENT_TYPE.FLOAT_BELOW] as const) {
		const root = card.elements?.[CARD_ROOT_ELEMENT_INDEX[type]];
		if (!root || !Array.isArray(root.children)) continue;
		const storyRoot = storyElements[STORY_ROOT_ELEMENT_INDEX[type]];
		const canonical = Array.isArray(storyRoot?.children)
			? storyRoot.children.filter((child): child is BBModel => !!child && '_id' in child)
			: [];
		const ids = new Set(canonical.map(model => model._id));
		const seen = new Set<string>();
		const children: CardFloatChild[] = [];
		for (const child of root.children) {
			const id = getStoryElementId(child);
			if (id !== undefined && ids.has(id)) {
				if (!seen.has(id))
					children.push(
						isStoryElementReference(child) && Object.keys(child).length === 1
							? child
							: createStoryElementReference(id)
					);
				seen.add(id);
			} else if (!isStoryElementReference(child)) {
				// Old full global copies without a canonical owner were hidden/removed by legacy sync.
				// Keep malformed entries for StoryDoctor and retain ordinary local/symbol placements.
				if (migrateLegacy && child && '_id' in child && child.uiConfig?.layer === LAYER_TYPE.FIXED_GLOBAL)
					continue;
				children.push(child);
			}
		}
		for (const model of canonical) {
			if (!seen.has(model._id)) children.push(createStoryElementReference(model._id));
		}
		if (
			children.length !== root.children.length ||
			children.some((child, index) => child !== root.children[index])
		) {
			root.children = children;
		}
	}
}

export function synchronizeStoryReferences(data: Pick<StoryVersionType['data'], 'steps' | 'elements'>): void {
	data.steps?.forEach(step => step.cards.forEach(card => synchronizeStoryElementReferences(card, data.elements)));
}

/** Convert persisted legacy placements only at the version-gated Admin migration boundary. */
export function migrateStoryReferences(data: Pick<StoryVersionType['data'], 'steps' | 'elements'>): void {
	data.steps?.forEach(step =>
		step.cards.forEach(card => normalizeStoryElementReferences(card, data.elements ?? [], true))
	);
}

/** Canonical membership, including descendants, is the ownership test. */
export function findStoryElement(storyElements: BBModel[], id: string) {
	return findComponentBy(storyElements, { path: '_id', value: id });
}
